import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

import PanelTemplate from "../../templates/Panel";
import PageHeader from "../../components/PageHeader";
import BoxTabs from "../../components/BoxTabs";
import Button from "../../components/Button";
import Input from "../../components/Input";
import InputSelect from "../../components/InputSelect";
import Toggle from "../../components/Toggle";
import CreditCoin from "../../components/CreditCoin";
import api from "../../services/api";
import { renderError } from "../../helpers/errors";
import { refreshCredits } from "../../services/credits";

import "./styles.scss";

const periodOptions = [
    { value: 'month', label: 'Por mês' },
    { value: 'total', label: 'Acumulado (não reseta)' },
    { value: 'none', label: 'Sem limite' }
];

/**
 * Distribuição dos créditos (BDM-1058).
 *
 * O saldo é um só, da plataforma: aqui se define quanto cada pessoa pode tirar
 * dele. É o texto que abre a tela, porque a pergunta que todo mundo faz é "cada
 * um tem a sua carteira?".
 */
export const CreditAllocation = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [wallet, setWallet] = useState({
        low_balance_threshold: 0,
        allow_personal_reserve: true,
        allow_spend_request: true
    });
    const [roles, setRoles] = useState([]);
    const [exceptions, setExceptions] = useState([]);
    const [people, setPeople] = useState([]);

    useEffect(() => {
        load();
    }, []);

    const load = () => {
        setLoading(true);

        api.get('/credit/allocation').then(res => {
            const resource = res.data.resource;
            setWallet(resource.wallet);
            setRoles(resource.roles);
            setExceptions(resource.exceptions);
            setPeople(resource.people);
        }).catch(renderError).then(() => setLoading(false));
    }

    const save = () => {
        setSaving(true);

        api.put('/credit/allocation', {
            wallet: {
                ...wallet,
                low_balance_threshold: parseInt(wallet.low_balance_threshold) || 0
            },
            roles: roles.map(role => ({
                role_id: role.role_id,
                can_spend: role.can_spend,
                limit_credits: role.period === 'none' || !parseInt(role.limit_credits) ? null : parseInt(role.limit_credits),
                period: role.period
            })),
            exceptions: exceptions.map(item => ({
                admin_id: item.admin_id,
                can_spend: item.can_spend,
                limit_credits: item.period === 'none' || !parseInt(item.limit_credits) ? null : parseInt(item.limit_credits),
                period: item.period,
                reserved_credits: parseInt(item.reserved_credits) || 0
            }))
        }).then(res => {
            const resource = res.data.resource;
            setWallet(resource.wallet);
            setRoles(resource.roles);
            setExceptions(resource.exceptions);
            refreshCredits();
            toast.success('Distribuição salva');
        }).catch(error => {
            renderError(error);
        }).then(() => setSaving(false));
    }

    const updateRole = (index, changes) => {
        setRoles(roles.map((role, position) => position === index ? { ...role, ...changes } : role));
    }

    const updateException = (index, changes) => {
        setExceptions(exceptions.map((item, position) => position === index ? { ...item, ...changes } : item));
    }

    const addException = adminId => {
        if (!adminId || exceptions.some(item => item.admin_id === adminId)) return;

        const person = people.find(item => item.id === adminId);

        setExceptions([...exceptions, {
            admin_id: adminId,
            name: person?.name,
            can_spend: true,
            limit_credits: '',
            period: 'month',
            reserved_credits: 0,
            spent: 0
        }]);
    }

    const rolesTab = (
        <div className="table-responsive">
            <table className="table">
                <thead>
                    <tr>
                        <th>Nível de acesso</th>
                        <th>Pode usar créditos</th>
                        <th>Limite por pessoa <span className="th-note">em branco = sem limite</span></th>
                        <th>Período</th>
                    </tr>
                </thead>
                <tbody>
                    {roles.map((role, index) => (
                        <tr key={role.role_id}>
                            <td>
                                {role.name}
                                <span className="cell-note">{role.people} pessoa{role.people === 1 ? '' : 's'}</span>
                            </td>
                            <td>
                                <Toggle checked={role.can_spend} onChange={value => updateRole(index, { can_spend: value })}
                                />
                            </td>
                            <td>
                                {role.can_spend && role.period !== 'none'
                                    ? <Input
                                        value={role.limit_credits || ''}
                                        mask={`convertToInt`}
                                        change={value => updateRole(index, { limit_credits: value || '' })}
                                    />
                                    : <span className="cell-note">—</span>}
                            </td>
                            <td>
                                {role.can_spend
                                    ? <InputSelect
                                        value={role.period}
                                        options={periodOptions}
                                        change={value => updateRole(index, { period: value })}
                                    />
                                    : <span className="cell-note">sem acesso</span>}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    const exceptionsTab = (
        <>
            <div className="add-exception">
                <InputSelect
                    label={`Adicionar pessoa`}
                    value={``}
                    options={people
                        .filter(person => !exceptions.some(item => item.admin_id === person.id))
                        .map(person => ({ value: person.id, label: `${person.name}${person.role ? ` · ${person.role}` : ''}` }))}
                    change={addException}
                />
            </div>

            {exceptions.length === 0 &&
                <p className="empty-note">Sem exceções: todo mundo segue a regra do próprio nível de acesso.</p>
            }

            {exceptions.length > 0 &&
                <div className="table-responsive">
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Pessoa</th>
                                <th>Pode usar</th>
                                <th>Limite <span className="th-note">em branco = sem limite</span></th>
                                <th>Período</th>
                                {wallet.allow_personal_reserve && <th>Reserva pessoal</th>}
                                <th className="right">Consumo</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {exceptions.map((item, index) => (
                                <tr key={item.admin_id}>
                                    <td>{item.name}</td>
                                    <td>
                                        <Toggle checked={item.can_spend} onChange={value => updateException(index, { can_spend: value })} />
                                    </td>
                                    <td>
                                        {item.can_spend && item.period !== 'none'
                                            ? <Input value={item.limit_credits || ''} mask={`convertToInt`} change={value => updateException(index, { limit_credits: value || '' })} />
                                            : <span className="cell-note">—</span>}
                                    </td>
                                    <td>
                                        {item.can_spend
                                            ? <InputSelect value={item.period} options={periodOptions} change={value => updateException(index, { period: value })} />
                                            : <span className="cell-note">bloqueada</span>}
                                    </td>
                                    {wallet.allow_personal_reserve &&
                                        <td>
                                            <Input value={item.reserved_credits ?? 0} mask={`convertToInt`} change={value => updateException(index, { reserved_credits: value })} />
                                        </td>
                                    }
                                    <td className="right">
                                        {item.spent}
                                        {item.can_spend && item.period !== 'none' && parseInt(item.limit_credits) > 0 &&
                                            <span className="cell-note">de {parseInt(item.limit_credits)}{item.period === 'month' ? ' no mês' : ''}</span>
                                        }
                                    </td>
                                    <td className="right">
                                        <Button
                                            type={`transparent`}
                                            size={`small`}
                                            text={`Remover`}
                                            action={() => setExceptions(exceptions.filter((_, position) => position !== index))}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            }

            {wallet.allow_personal_reserve &&
                <p className="reserve-note">
                    <CreditCoin size={16} />
                    A reserva pessoal sai do saldo comum e só o dono gasta. Um administrador pode devolvê-la ao saldo a qualquer momento.
                </p>
            }
        </>
    );

    const rulesTab = (
        <>
            <div className="switch-row">
                <div>
                    <p className="switch-title">Permitir reserva pessoal</p>
                    <p className="switch-note">Parte do saldo fica separada para uma pessoa específica.</p>
                </div>
                <Toggle checked={wallet.allow_personal_reserve} onChange={value => setWallet({ ...wallet, allow_personal_reserve: value })} />
            </div>

            <div className="switch-row">
                <div>
                    <p className="switch-title">Deixar pedir crédito</p>
                    <p className="switch-note">Sem saldo, quem não pode comprar vê "pedir para um administrador" em vez de um erro.</p>
                </div>
                <Toggle checked={wallet.allow_spend_request} onChange={value => setWallet({ ...wallet, allow_spend_request: value })} />
            </div>

            <div className="rules-fields">
                <Input
                    label={`Avisar quando o saldo chegar a`}
                    value={wallet.low_balance_threshold ?? 0}
                    mask={`convertToInt`}
                    change={value => setWallet({ ...wallet, low_balance_threshold: value })}
                />
            </div>
        </>
    );

    return (
        <PanelTemplate id="credit-allocation">
            <PageHeader
                left={<h1>Distribuição de créditos</h1>}
                right={<Button type={`primary`} size={`small`} svg={`save-white`} text={`Salvar`} action={save} loading={saving || loading} />}
            />

            <div className="box-alert-info">
                O saldo é um só, da plataforma. O que se configura aqui é <b>quanto cada pessoa pode tirar dele</b> —
                assim nenhum crédito fica preso na carteira de quem não usa.
            </div>

            <BoxTabs
                titles={['Por nível de acesso', 'Exceções por pessoa', 'Quando o saldo acabar']}
                contents={[rolesTab, exceptionsTab, rulesTab]}
            />
        </PanelTemplate>
    );
}

export default CreditAllocation;
